import {
    BgColorsOutlined,
    CaretUpOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SendOutlined,
} from '@ant-design/icons'
import { Alert, CollapseProps, Image, Table, Typography } from 'antd'

const { Title, Text, Paragraph } = Typography

const assemblyExampleColumns = [
    { title: 'Наименование', dataIndex: 'name', key: 'name' },
    { title: 'Тираж', dataIndex: 'circulation', key: 'circulation', width: 90 },
    { title: 'Ручьи в сборке', dataIndex: 'streams', key: 'streams', width: 130 },
    { title: 'Сборка', dataIndex: 'assembly', key: 'assembly', width: 90 },
]

const assemblyExampleData = [
    {
        key: '1',
        name: 'Крем-мыло «Ромашка» 500мл',
        circulation: 6000,
        streams: 2,
        assembly: 7,
    },
    {
        key: '2',
        name: 'Крем-мыло «Лаванда» 500мл',
        circulation: 3000,
        streams: 1,
        assembly: 7,
    },
    {
        key: '3',
        name: 'Крем-мыло «Алоэ» 500мл',
        circulation: 3000,
        streams: 1,
        assembly: 7,
    },
]

const colorProofPriceColumns = [
    { title: 'Надбавка', dataIndex: 'name', key: 'name' },
    { title: 'Цена, ₽', dataIndex: 'price', key: 'price', width: 120 },
]

const colorProofPriceData = [
    { key: 'cmyk', name: 'CMYK, за каждую краску', price: '3 500' },
    { key: 'pantones', name: 'Пантон, за каждый', price: '1 000' },
    { key: 'whitewash', name: 'Белила, за слой', price: '1 000' },
    { key: 'lamination', name: 'Ламинация', price: '1 000' },
    { key: 'solidLacquer', name: 'Лак сплошной, за каждый', price: '300' },
    { key: 'selectiveLacquer', name: 'Лак выборочный, за каждый', price: '500' },
    { key: 'embossing', name: 'Тиснение', price: '2 000' },
    { key: 'embossing3d', name: '3D-тиснение', price: '3 000' },
    { key: 'lacquerDms3d', name: '3D-лак DMS, за слой', price: '2 000' },
    { key: 'stencil', name: 'Трафарет', price: '5 000' },
    { key: 'congreve', name: 'Конгрев', price: '5 000' },
]

export const helpItems: CollapseProps['items'] = [
    {
        key: '1',
        label: <b>Быстрый старт</b>,
        children: (
            <>
                <p>
                    Раздел «Расчёты» — место, где менеджер собирает параметры заказа, получает
                    стоимость и время, после чего переносит заказ в отгрузки.
                </p>
                <ol>
                    <li>
                        Нажмите <Text strong>«Новый расчёт»</Text> и выберите клиента (или создайте
                        нового).
                    </li>
                    <li>
                        Заполните позицию: материал → штамп (или «Без выс») → станок → цветность →
                        тираж → цена продажи.
                    </li>
                    <li>
                        Проверьте индикатор справа: зелёная галочка — можно идти дальше; красный
                        восклицательный знак — исправьте ошибки в ячейках.
                    </li>
                    <li>
                        Если несколько видов печатаются вместе — объедините их номером в столбце
                        «Сборка» (удобно копировать позицию).
                    </li>
                    <li>
                        Нажмите синюю кнопку со стрелкой — откроется форма переноса в отгрузки.
                        Заполните исходники, материалы, намотку и отправьте.
                    </li>
                </ol>
                <Alert
                    showIcon
                    type='info'
                    style={{ marginTop: 8 }}
                    message='Перед переносом в отгрузки убедитесь, что у позиции нет красного индикатора и заполнены ключевые поля.'
                />
            </>
        ),
    },
    {
        key: '2',
        label: <b>Создание расчёта и позиции</b>,
        children: (
            <>
                <p>
                    Для создания расчёта нажмите «Новый расчёт» и выберите клиента из списка или
                    создайте нового.
                </p>
                <Image src='/guides/calculations/создание расчета.png' alt='Создание расчёта' />
                <p>
                    Если вы создаёте нового клиента, для продолжения оформления отгрузки нужно
                    направить информацию о клиенте в отдел логистики.
                </p>
                <p>После создания в расчёте будет одна позиция.</p>

                <Title level={3}>Позиции расчёта</Title>
                <p>
                    Расчёт состоит из позиций. Добавляйте, копируйте и удаляйте их кнопками справа:
                </p>
                <ul>
                    <li>
                        <Text strong>«+»</Text> — добавить пустую позицию
                    </li>
                    <li>
                        <Text strong>Копировать</Text> — дублировать заполненную (удобно для сборок)
                    </li>
                    <li>
                        <Text strong>Стрелка</Text> — перенести позицию или сборку в отгрузки
                    </li>
                    <li>
                        <Text strong>Удалить</Text> — удалить позицию (или весь расчёт на строке
                        расчёта)
                    </li>
                </ul>
                <Image
                    src='/guides/calculations/добавление позиции.png'
                    alt='Добавление позиции расчёта'
                />
                <p>Чтобы изменить параметр — кликните по ячейке и укажите значение.</p>

                <Title level={3}>Фильтры списка</Title>
                <p>
                    В шапке страницы можно отфильтровать расчёты по номеру позиции, заказчику,
                    наименованию вида, материалу, месяцу создания, ширине роля и номеру штампа.
                    Кнопка «Сброс» очищает фильтры.
                </p>
            </>
        ),
    },
    {
        key: '3',
        label: <b>Штамп и печать без высечки</b>,
        children: (
            <>
                <Title level={3}>Поиск существующего штампа</Title>
                <p>
                    Кликните на ячейку штампа → появится контрол с номером и кнопкой поиска →
                    нажмите поиск. Откроется модальное окно работы со штампами.
                </p>
                <p>
                    Укажите фильтры (ширину, длину, форму). Найдя подходящий штамп, нажмите кнопку
                    справа.
                </p>
                <Image src='/guides/calculations/поиск штампа.png' alt='Поиск штампа' />
                <p>После выбора информация о штампе попадёт в строку позиции.</p>
                <p>
                    Если точного совпадения нет, система предложит похожие варианты — смотрите на
                    заголовок списка.
                </p>
                <Image src='/guides/calculations/выбор похожего штампа.png' alt='Похожие штампы' />

                <Title level={3}>Новый штамп</Title>
                <p>
                    Если нужного штампа нет, укажите характеристики в форме создания, оцените
                    стоимость (она меняется при изменении параметров) и нажмите «Использовать
                    новый».
                </p>
                <Image
                    src='/guides/calculations/выбор нового штампа.png'
                    alt='Выбор нового штампа'
                />
                <p>
                    Новые штампы (ещё без заказов) помечаются синей стрелкой вверх:{' '}
                    <CaretUpOutlined style={{ color: '#1677ff' }} />
                </p>
                <Image src='/guides/calculations/созданный штамп.png' alt='Созданный штамп' />
                <p>
                    При наведении на номер штампа открывается подсказка: форма, тубус, стоимость
                    (для новых), размеры, ручьи, межэтикетка и др.
                </p>

                <Title level={3}>Печать без высечки («Без выс»)</Title>
                <ol>
                    <li>Поставьте галочку в столбце «Без выс».</li>
                    <li>
                        Кликните по ячейке штампа — откроется модалка{' '}
                        <Text strong>виртуального штампа</Text> (ширина × длина, ручьи, вал).
                    </li>
                    <li>Заполните параметры печати вручную.</li>
                </ol>
                <Alert
                    showIcon
                    type='info'
                    style={{ marginTop: 8 }}
                    message='При «Без выс» обычный поиск штампа недоступен — вместо него заполняется виртуальный штамп.'
                />
            </>
        ),
    },
    {
        key: '4',
        label: <b>Материалы, станки и цветность</b>,
        children: (
            <>
                <Title level={3}>Материалы</Title>
                <p>
                    Есть <Text strong>основной материал</Text> (один) и{' '}
                    <Text strong>дополнительные</Text> (несколько). Сначала выберите тип(ы)
                    материала, затем ширины.
                </p>
                <p>Корректировки ширин указываются для каждого типа материала отдельно.</p>
                <Alert
                    showIcon
                    type='warning'
                    style={{ margin: '8px 0 16px' }}
                    message='При смене типа основного или доп. материала выбранные ширины сбрасываются — укажите их заново.'
                />
                <p>
                    Нельзя рассчитать на материале, которого нет в системе. Чтобы завести новый тип
                    — обратитесь к администратору.
                </p>

                <Title level={3}>Станки</Title>
                <p>
                    Печатный станок — один; отделочных станков может быть несколько. Если станок
                    несовместим с выбранным валом, система заблокирует его в списке.
                </p>
                <ul>
                    <li>
                        <Text strong>Durst</Text> — система может потребовать выбрать GF (ячейка
                        подсветится красным).
                    </li>
                    <li>
                        <Text strong>Durst / WG</Text> — нужно указать «Кол-во видов Durst»
                        (подсветка красным).
                    </li>
                </ul>

                <Title level={3}>Цветность и доп. работы</Title>
                <p>
                    Укажите CMYK, пантоны, лак, белила, лаки DMS и дополнительные работы — они
                    влияют на расчёт стоимости и времени.
                </p>
            </>
        ),
    },
    {
        key: '5',
        label: <b>Сборки и кол-во видов Durst</b>,
        children: (
            <>
                <Title level={3}>Сборки</Title>
                <p>
                    Сборка — несколько видов, печатающихся вместе в разных ручьях. Чтобы объединить
                    позиции, укажите одинаковый номер в столбце «Сборка» (например, «1»).
                </p>
                <p>
                    Пример: в расчёте 5 позиций, 3 из них с номером сборки «1» — при переносе в
                    отгрузки они уйдут одной группой (одна вкладка / одна отгрузка).
                </p>
                <p>
                    Параметры позиций в сборке обычно одинаковые — удобно пользоваться «Копировать
                    позицию».
                </p>
                <Alert
                    showIcon
                    type='info'
                    style={{ margin: '8px 0 16px' }}
                    message='Автоматическое формирование сборок пока не реализовано — номер сборки указывайте вручную.'
                />

                <Title level={3}>Кол-во видов Durst</Title>
                <p>
                    Для предварительного расчёта можно указать число видов. При переносе в отгрузки
                    позиция разбивается на это количество видов: понадобится указать наименования,
                    тиражи и исходные файлы в форме переноса.
                </p>
            </>
        ),
    },
    {
        key: '6',
        label: <b>Как работать со сборками (подробно)</b>,
        children: (
            <>
                <p>
                    В системе есть 3 поля, относящиеся к объединениям видов, с которыми работает
                    система:
                </p>
                <ul>
                    <li>
                        <Text strong>Сборка</Text>
                    </li>
                    <li>
                        <Text strong>Ручьи в сборке</Text> — пока разве что для дизайнера, возможно
                        потом будем выводить в производственном бланке
                    </li>
                    <li>
                        <Text strong>Кол-во видов Durst</Text>
                    </li>
                </ul>
                <Image
                    src='/guides/calculations/столбцы сборок.png'
                    alt='Столбцы Сборка, Ручьи в сборке, Кол-во видов Durst'
                />

                <Title level={3}>По умолчанию</Title>
                <p>1 вид — одна строка — одна позиция расчёта.</p>
                <p>
                    Идём от простого к сложному. Каждая строка рассчитывается индивидуально, это
                    один вид = одна этикетка.
                </p>

                <Title level={3}>Расчёт на флексу</Title>
                <p>
                    Вы имеете несколько схожих видов и хотели бы объединить их — необходимо
                    действовать следующим образом:
                </p>
                <ol>
                    <li>Заполняете одну строку параметрами.</li>
                    <li>
                        Дублируете (там есть кнопка), создавая столько строк, сколько видов вам
                        нужно.
                    </li>
                    <li>
                        Меняете <Text strong>Наименование</Text>, <Text strong>Тираж</Text> и{' '}
                        <Text strong>Ручьи в сборке</Text> (какое кол-во ручьёв займёт конкретный
                        вид).
                    </li>
                    <li>
                        Все виды, которые вы хотите объединить, должны быть под одним номером — всем
                        им указываете номер сборки одно и то же число (это можно сделать на 1
                        этапе).
                    </li>
                </ol>
                <p>
                    При переводе в отгрузку все виды (строки) с одним числом в столбце «Сборка»
                    пойдут в одну сборку: у этих видов будет одна отгрузка с выбранными видами в
                    качестве позиций, будет общий бланк. Одна отгрузка = одна сборка по видам.
                </p>
                <Image
                    src='/guides/calculations/расчет переводится в отгрузки.png'
                    alt='Перевод расчёта в отгрузки'
                />

                <Title level={4}>Пример</Title>
                <p>
                    Заказчик прислал 3 похожих вида крем-мыла (один материал, один штамп/высечка,
                    один станок, одна цветность), которые вы хотите отпечатать одним прогоном на
                    флексомашине.
                </p>
                <Paragraph>
                    <Text strong>Шаг 1–2.</Text> Заполняете первую строку (материал, штамп, станок,
                    цветность и т.д.), жмёте «Дублировать» 2 раза — получаете 3 одинаковые строки.
                </Paragraph>
                <Paragraph>
                    <Text strong>Шаг 3.</Text> Меняете в каждой строке Наименование, Тираж и Ручьи в
                    сборке:
                </Paragraph>
                <Table
                    size='small'
                    pagination={false}
                    columns={assemblyExampleColumns}
                    dataSource={assemblyExampleData}
                />
                <p>
                    «Ручьи в сборке» — это сколько ручьёв (дорожек на валу/штампе) займёт конкретный
                    вид. В примере вал на 4 ручья: 2 + 1 + 1 = 4. Система корректность указанных
                    вами ручьёв <Text strong>не проверяет</Text> — это ваш ручной расчёт, чтобы не
                    превысить ручьи штампа/вала.
                </p>
                <Paragraph>
                    <Text strong>Шаг 4.</Text> Во всех трёх строках указываете одно и то же число в
                    столбце «Сборка» — в примере «7» (можно сразу проставить на 1 строке до
                    дублирования, чтобы не повторять).
                </Paragraph>

                <Title level={4}>Результат при переводе в отгрузку</Title>
                <p>
                    Все 3 строки (т.к. у них одинаковая «Сборка» = 7) уйдут в{' '}
                    <Text strong>одну</Text> отгрузку с тремя позициями: «Ромашка» (6000 шт),
                    «Лаванда» (3000 шт), «Алоэ» (3000 шт). Бланк (макет) будет общий один на все 3
                    вида, т.к. они печатаются вместе на одном прогоне.
                </p>
                <p>
                    Если бы «Сборка» не была указана (или у видов были разные номера) — получилось
                    бы 3 отдельные отгрузки с отдельными бланками.
                </p>

                <Title level={4}>Зачем это?</Title>
                <p>
                    В будущем будет кнопка «Сформировать сборки»: вы сможете создать хоть 30 позиций
                    в расчёт с разными тиражами. Система сама отсортирует их по ручьям с учётом
                    количества копий (тиражей) и предложит вам лучший вариант. Не нужно будет
                    думать, как лучше скомбинировать сборки.
                </p>
                <Alert
                    showIcon
                    type='info'
                    style={{ margin: '8px 0 16px' }}
                    message='Автоматическое формирование сборок пока не реализовано — номер сборки указывайте вручную.'
                />

                <Title level={3}>Расчёт на цифру</Title>
                <p>
                    Тут как раз исключение: в одну строку закладываете 1 или несколько видов,
                    фиксируя общий тираж (для скорости расчётов).
                </p>
                <ol>
                    <li>Создаёте одну строку, конфигурируете.</li>
                    <li>
                        Указываете <Text strong>«Кол-во видов Durst»</Text> (сколько видов вы
                        закладываете в этой строке).
                    </li>
                </ol>
                <Alert
                    showIcon
                    type='warning'
                    style={{ margin: '8px 0 16px' }}
                    message='Столбцы «Сборка» и «Ручьи в сборке» указывать не нужно, иначе система начнёт группировать и объединять тиражи для расчёта.'
                />
                <p>
                    При переводе в отгрузку будет создана одна отгрузка и столько позиций, сколько
                    указано в столбце «Кол-во видов Durst».
                </p>
            </>
        ),
    },
    {
        key: '7',
        label: <b>Перенос в отгрузки</b>,
        children: (
            <>
                <p>
                    Нажмите синюю кнопку со стрелкой «Перенести в отгрузки» — у строки расчёта (весь
                    расчёт) или у позиции (одна позиция / сборка).
                </p>

                <Title level={3}>Позиция vs весь расчёт</Title>
                <ul>
                    <li>
                        <Text strong>Одна позиция</Text> — в форме слева одна вкладка, данные только
                        по этой позиции.
                    </li>
                    <li>
                        <Text strong>Весь расчёт</Text> — вкладки по сборкам/позициям. Одна вкладка
                        = одна будущая отгрузка.
                    </li>
                </ul>
                <p>
                    Позиции с одинаковым номером в «Сборка» объединяются в одну группу. Если указано
                    «Кол-во видов Durst», при переносе создаётся столько позиций отгрузки, сколько
                    видов.
                </p>

                <Title level={3}>Повтор бланка</Title>
                <p>
                    Если у позиции уже была отгрузка с бланком, появится галочка «Макет не
                    изменился, использовать бланк из отгрузки №…». Оставьте её, если макет тот же —
                    исходники загружать не нужно. Снимите, если макет изменился и дизайнеру нужен
                    новый бланк.
                </p>

                <Title level={3}>Верхняя часть формы</Title>
                <Image
                    src='/guides/calculations/форма перевода в отгрузки 1.png'
                    alt='Форма переноса в отгрузки'
                />
                <ul>
                    <li>
                        <Text strong>Общий файл для отгрузки (исходники)</Text> — файл от клиента.
                        Если несколько видов в одном файле — загружайте сюда.
                    </li>
                </ul>
                <p>
                    <Text strong>Для каждого вида:</Text>
                </p>
                <ul>
                    <li>
                        <Text strong>Наименование</Text> — как вид будет отображаться в системе.
                        Рекомендация: заголовок 1 уровня на этикетке + заголовки 2–3 уровня + мера +
                        версия.
                        <br />
                        Хорошие примеры: «Fairy Лимон 450 мл», «Fairy Лимон 450мл в1», «Активия
                        Йогурт Клубника 150г в5».
                        <Alert
                            showIcon
                            style={{ marginTop: 8 }}
                            type='info'
                            message='Корректные имена сразу упрощают поиск при повторе заказа. Позже планируется структура «Клиент → Виды → Версии → Заказы».'
                        />
                    </li>
                    <li>
                        <Text strong>Тираж</Text> — тираж конкретного вида.
                    </li>
                    <li>
                        <Text strong>Комментарий</Text> — к виду.
                    </li>
                    <li>
                        <Text strong>Исходник от клиента</Text> — если в файле один вид, лучше
                        загрузить сюда: файл привяжется к позиции.
                    </li>
                </ul>
                <Alert
                    showIcon
                    type='warning'
                    style={{ margin: '8px 0 16px' }}
                    message='Обязательно: общий файл отгрузки или исходник у каждого вида (кроме случая повторного бланка).'
                />

                <p>
                    <Text strong>Дата поставки клиенту</Text> — обязательное поле.
                </p>

                <Title level={3}>Материалы</Title>
                <Image
                    src='/guides/calculations/форма перевода в отгрузки 2 материалы.png'
                    alt='Резервирование материалов'
                />
                <p>
                    Сконфигурируйте материалы для печати. Для каждого типа можно указать несколько
                    ширин (например, 1 км из остатка + дозаказ).
                </p>
                <ul>
                    <li>
                        <Text strong>Ширина</Text> — из списка (с остатком) или новая (система
                        создаст).
                    </li>
                    <li>
                        <Text strong>Количество</Text> — сколько метража под это действие.
                    </li>
                    <li>
                        <Text strong>Действие</Text> — «Зарезервировать» (закрепить за заказом) или
                        «Заказать» (заявка оператору).
                    </li>
                </ul>
                <p>
                    Справа вверху блока: сколько уже сконфигурировано и сколько нужно по расчёту.
                    Действия выполняются при <Text strong>запуске отгрузки</Text> (см. помощь в
                    разделе «Отгрузки»).
                </p>

                <Title level={3}>Нижняя часть формы</Title>
                <Image
                    src='/guides/calculations/форма перевода в отгрузки 3.png'
                    alt='Нижняя часть формы переноса'
                />
                <ul>
                    <li>
                        <Text strong>Дизайнер</Text> — обрабатывает исходники; при запуске отгрузки
                        задачи (бланк, штамп, формы) создаются на него.
                    </li>
                    <li>
                        <Text strong>Схема намотки (лицо / оборот)</Text> — выберите схему по
                        картинке.
                    </li>
                    <li>
                        <Text strong>Намотка по</Text> — количество этикеток в рулоне. При наведении
                        на название поля — подсказка с возможными намотками для тиража и ручьёв.
                    </li>
                    <li>
                        <Text strong>Инфо по намотке</Text> — свободный текст, например «Строго» или
                        «краевые ролики по 200». Выводится в клиентском и производственном бланках.
                    </li>
                    <li>
                        <Text strong>Втулка</Text> — 40 / 45 / 76 / 152 мм.
                    </li>
                    <li>
                        <Text strong>Печать на клеевом слое</Text> — актуально при доп. работе
                        «Переворот».
                    </li>
                    <li>
                        <Text strong>
                            Упаковка, тираж упаковки, этикеровка, ярлык, паллет, конгрев,
                            праймирование
                        </Text>{' '}
                        — параметры отгрузки.
                    </li>
                    <li>
                        <Text strong>Комментарий к заданию</Text> — свободный текст для производства
                        / дизайна.
                    </li>
                </ul>

                <Title level={3}>После отправки</Title>
                <p>
                    Система создаст отгрузки, отметит позиции иконкой{' '}
                    <SendOutlined style={{ color: '#52c41a' }} /> и при необходимости создаст задачи
                    на отрисовку бланков. В уведомлении будет ссылка на раздел «Отгрузки».
                </p>
            </>
        ),
    },
    {
        key: '8',
        label: <b>Цветопробы</b>,
        children: (
            <>
                <p>
                    Чтобы позиция считалась как цветопроба, поставьте галочку в колонке «ЦП». Такая
                    позиция помечается иконкой <BgColorsOutlined style={{ color: '#d71868' }} /> в
                    расчётах, отгрузках и графиках печати.
                </p>

                <Title level={3}>Как считается цена</Title>
                <p>
                    Отпускная цена цветопробы рассчитывается автоматически — вручную её ввести
                    нельзя. Цена складывается из надбавок:
                </p>
                <Table
                    columns={colorProofPriceColumns}
                    dataSource={colorProofPriceData}
                    pagination={false}
                    size='small'
                    bordered
                />
                <p style={{ marginTop: 16 }}>Какие надбавки учитываются, зависит от станка:</p>
                <ul>
                    <li>
                        <b>Флексо</b> — все позиции.
                    </li>
                    <li>
                        <b>Durst с GF в отделке</b> — CMYK, белила, ламинация, лаки, 3D-лак DMS,
                        3D-тиснение. Пантоны, тиснение, трафарет и конгрев в цену не входят.
                    </li>
                    <li>
                        <b>Durst без GF (выкатка)</b> — только CMYK и белила. Лаки, пантоны, доп.
                        работы и лак DMS заблокированы.
                    </li>
                    <li>
                        <b>DCMQ</b> — цена цветопробы не рассчитывается.
                    </li>
                </ul>
                <Alert
                    type='info'
                    showIcon
                    message='Если снять галочку «ЦП», позиция снова станет этикеткой, а отпускная цена очистится — её нужно ввести заново.'
                />

                <Title level={3}>Что скрыто или отключено</Title>
                <ul>
                    <li>
                        Не показываются «Себест. 1 шт», «Себестоимость тиража», «Прибыль с тиража» и
                        «Прибыль в 1 час». Остаётся только «Итого за тираж».
                    </li>
                    <li>
                        Проверки и подсветка ячеек к цветопробе не применяются — в колонке статуса
                        вместо индикатора стоит иконка цветопробы.
                    </li>
                    <li>У отгрузки-цветопробы нельзя поставить галочку «Сделка».</li>
                    <li>Кураторы не видят отгрузки-цветопробы в своём списке.</li>
                </ul>

                <Title level={3}>Перенос в отгрузки и заказ</Title>
                <p>
                    При переносе цветопробы в отгрузки система покажет открытые заказы клиента и
                    предложит добавить цветопробу в один из них или создать новый заказ. В заказе
                    она появится отдельной позицией с типом продукции «Цветопроба».
                </p>
            </>
        ),
    },
    {
        key: '9',
        label: <b>Индикаторы и обозначения</b>,
        children: (
            <>
                <Title level={3}>Статус валидации</Title>
                <p>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} /> Все проверки
                    пройдены.
                    <br />
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} /> Есть
                    ошибки конфигурации или некорректные расчётные показатели (маржа, ширина
                    материала, станок, эффективность и др.). Наведите на иконку — увидите, сколько
                    ячеек с ошибками.
                </p>
                <p>
                    Красная подсветка ячеек обычно означает обязательное незаполненное или
                    несовместимое поле (например, Durst без GF / без кол-ва видов).
                </p>

                <Title level={3}>Перенос в отгрузки</Title>
                <p>
                    <SendOutlined style={{ color: '#52c41a', fontSize: 16 }} /> Позиция уже
                    перенесена в отгрузки.
                </p>

                <Title level={3}>Новый штамп</Title>
                <p>
                    <CaretUpOutlined style={{ color: '#1677ff', fontSize: 16 }} /> У штампа ещё не
                    было заказов.
                </p>

                <Title level={3}>Цвет строки</Title>
                <p>
                    В первой узкой колонке можно задать цвет позиции — строка подсветится этим
                    цветом. Удобно визуально группировать позиции в большом расчёте.
                </p>
            </>
        ),
    },
    {
        key: '10',
        label: <b>Частые вопросы и ошибки</b>,
        children: (
            <>
                <Title level={3}>Почему сбросились ширины материала?</Title>
                <p>
                    Вы изменили тип основного или дополнительного материала. Ширины нужно выбрать
                    заново.
                </p>

                <Title level={3}>Почему станок недоступен?</Title>
                <p>
                    Он несовместим с валом выбранного штампа (или виртуального штампа). Смените вал
                    / штамп или выберите другой станок.
                </p>

                <Title level={3}>Красный индикатор не уходит</Title>
                <p>Проверьте по очереди:</p>
                <ul>
                    <li>заполнены ли штамп (или виртуальный штамп при «Без выс»);</li>
                    <li>выбраны ли материал и ширины;</li>
                    <li>указан ли печатный станок;</li>
                    <li>для Durst / WG — GF и/или кол-во видов;</li>
                    <li>не выходят ли за допустимые пределы маржа и другие расчётные поля.</li>
                </ul>

                <Title level={3}>Форма переноса пишет «Проверьте указанные данные»</Title>
                <p>
                    Вкладки с ошибками подсвечены красным слева. Чаще всего не хватает исходников,
                    даты поставки, материалов, намотки, втулки или обязательных радиокнопок внизу
                    формы.
                </p>

                <Title level={3}>Повтор заказа</Title>
                <p>
                    Повтор удобнее начинать из раздела «Отгрузки» («Повторить заказ») — создаётся
                    расчёт на основе прошлой отгрузки. При переносе снова можно переиспользовать
                    бланк, если макет не менялся.
                </p>
            </>
        ),
    },
]
